import { Deduction } from "@/core/entities/deduction";
import { useConfirm, useLookup, useUserPermissions, useWorkspace } from "@/hooks";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import {
  Table,
  TableColumnType,
  Flex,
  Avatar,
  Division,
  Highlighter,
  Button,
  Icon,
  Icons,
  Checkbox,
  Span,
  Tooltip,
} from "@ir/ui";
import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { WorkSpaceSettings } from "@/core/entities/workspace";
import { IDeductionColumns } from "@/core/repositories/deduction";
import { ProgressChip, ValidityChip } from "@/presentation/deduction/components";
import { DeductionUserDisplay } from "@/presentation/deduction/components/DeductionUserDisplay";
import classNames from "classnames";
import { useApproval } from "../hooks";

import "../styles/approvals.scss";
import { WorkspacePermissions } from "@/core/permissions";
import {
  buildColumnsFromConfig,
  confirmDiscardUnsavedChanges,
  getBlankDeductions,
  handleSelectRowCheckbox,
  openDeductionDrawerFromRow,
  parseSearchKeywords,
} from "@/presentation/deduction/utils";
import { ClickableTableCell, SkeletonCell } from "@/presentation/ui/components/common";
const APPROVAL_VIEW_PERMISSIONS = [WorkspacePermissions.DEDUCTIONS_APPROVAL_VIEW];

interface ApprovalTableProps {
  contentHeight: number;
  onApprovalModalOpen: (record: Deduction) => void;
  deductions?: Deduction[];
  displayMode?: "standalone" | "expanded-table";
}

interface ApprovalTableColumn {
  workspaceSettings: WorkSpaceSettings | null;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  columns: IDeductionColumns[];
  isRefreshing: boolean;
  searchKeywords: string[];
  onRowSelect: (record: Deduction) => Promise<void>;
  onSelectCheckbox: (record: Deduction, checked: boolean) => void;
  selectedApprovals: Deduction[];
  onApprovalModalOpen: (record: Deduction) => void;
}

const getColumns = (props: ApprovalTableColumn): TableColumnType<Deduction>[] => {
  const {
    workspaceSettings,
    hasAllPermissions,
    hasAnyPermission,
    columns,
    isRefreshing,
    searchKeywords,
    onRowSelect,
    onSelectCheckbox,
    selectedApprovals,
    onApprovalModalOpen,
  } = props;
  const canOpenApprovalModal = hasAnyPermission(APPROVAL_VIEW_PERMISSIONS);
  const tableColumns: TableColumnType<Deduction>[] = [
    {
      title: "",
      dataIndex: "id",
      key: "select",
      align: "center",
      width: 35,
      verticalAlign: "top",
      fixed: "left",
      hidden: !hasAllPermissions([
        WorkspacePermissions.DEDUCTIONS_APPROVAL_VIEW,
        WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION,
      ]),
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={20} width={20} />;
        }

        const isSelected = selectedApprovals.some(item => item.id === record.id);
        return (
          <ClickableTableCell
            record={record}
            onRowSelect={onRowSelect}
            isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable"
            style={{ padding: "6px 0" }}
          >
            <Checkbox
              size="medium"
              color="grey"
              slot="selection"
              checked={isSelected}
              onChange={(checked: boolean) => onSelectCheckbox(record, checked)}
            />
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Invoice Number",
      dataIndex: "claimNumber",
      key: "claimNumber",
      align: "left",
      verticalAlign: "top",
      width: 200,
      fixed: "left",
      // sortColumnKey: "claim_number",
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={120} />;
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            <Highlighter keywords={searchKeywords}>
              <Span className="font-medium">{record.claimNumber}</Span>
            </Highlighter>
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      verticalAlign: "top",
      width: 150,
      // sortColumnKey: "amount",
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={80} />;
        }
        const content = workspaceSettings?.displayNumberFormat
          ? record.amountFormatted(workspaceSettings.displayNumberFormat)
          : record.amount;
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            {content}
          </ClickableTableCell>
        );
      },
    },
    {
      title: "GL Code",
      dataIndex: "glCode",
      key: "glCode",
      align: "left",
      verticalAlign: "top",
      width: 200,
      // sortColumnKey: "gl_code",
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={180} />;
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            <Span style={{ whiteSpace: "pre-wrap" }}>{record.glCode}</Span>
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Approver",
      dataIndex: "approverUserFullName",
      key: "approverUserFullName",
      align: "left",
      verticalAlign: "top",
      width: 180,
      // sortColumnKey: "approver",
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={90} />;
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            <Flex align="center" className="display-user-wrapper" justifyContent="start" gap={0.5} wrap="no-wrap">
              <Avatar
                className={classNames("avatar-white", { "avatar-white-no-name": !record.approverUserFullName })}
                name={record.approverUserFullName ?? ""}
                shape="circle"
                size="extra-small"
                isDeleteUser={record.isApproverDeleted ?? undefined}
                title={record.isApproverDeleted ? "Removed User" : (record.approverUserFullName ?? "Unassigned")}
              />
              <Division className="name-display-container">
                <DeductionUserDisplay name={record.approverUserFullName} />
              </Division>
            </Flex>
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Validity",
      dataIndex: "validityName",
      key: "validityName",
      align: "left",
      verticalAlign: "top",
      width: 70,
      // sortColumnKey: "validity",
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={60} />;
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            <ValidityChip
              validityKey={record.validityKey}
              validityName={record.validityName}
              withBorder={true}
              withBackground={true}
              size="medium"
              shape="circle"
            />
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Progress",
      dataIndex: "stages",
      key: "stages",
      align: "left",
      verticalAlign: "top",
      width: 160,
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={80} />;
        }
        const stage = record.stages.find(stage => stage.isCurrent);
        if (!stage?.stageKey || !stage?.stageName || !record.status?.statusKey || !record.status?.statusName) {
          return null;
        }

        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            <ProgressChip
              stageKey={stage?.stageKey}
              stageName={stage?.stageName}
              statusKey={record.status?.statusKey}
              statusName={record.status?.statusName}
              withBorder={true}
              withBackground={true}
              shape="circle"
              size="medium"
            />
          </ClickableTableCell>
        );
      },
    },
    {
      title: "Comments",
      dataIndex: "latestApprovalComments",
      key: "latestApprovalComments",
      align: "left",
      verticalAlign: "top",
      width: 400,
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={120} />;
        }
        if (record.latestApprovalComments.length === 0) {
          return (
            <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
              -
            </ClickableTableCell>
          );
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable">
            {record.latestApprovalComments.map(comment => (
              <Fragment key={comment.messageId}>
                <Flex
                  className={`display-user-wrapper comment-row ${record.latestApprovalComments.length > 1 ? "mb-1" : ""}`}
                  align="center"
                  justifyContent="start"
                  wrap="no-wrap"
                  gap={0.25}
                >
                  <Avatar name={comment.senderFullName} title={comment.senderFullName} src={comment.senderAvatar} size="extra-small" shape="circle" className="avatar-white" />
                  <Division
                    className="text-grey-700 flex-1 min-w-0 comment-content font-medium ps-1"
                    style={{ minWidth: 0 }}
                  >
                    "{comment.contentHtml}"
                  </Division>
                </Flex>
              </Fragment>
            ))}
          </ClickableTableCell>
        );
      },
    },
    {
      title: "",
      dataIndex: "id",
      key: "actions",
      align: "right",
      verticalAlign: "top",
      width: 40,
      hidden: true,
      cellRender: (_value: unknown, record: Deduction) => {
        if (isRefreshing) {
          return <SkeletonCell height={10} width={20} />;
        }
        return (
          <ClickableTableCell record={record} onRowSelect={onRowSelect} isEnabled={canOpenApprovalModal}
            cellClassName="approval-table-cell-clickable d-flex flex-justify-end">
            <Division>
            <Tooltip content="Approve/Reject" placement="top right">
              <Button size="small" variant="clear" color="grey" onPress={() => onApprovalModalOpen(record)}>
                <Icon name={Icons.Send01} size="small" />
              </Button>
            </Tooltip>
            </Division>
          </ClickableTableCell>
        );
      },
    },
  ];

  return buildColumnsFromConfig(columns, tableColumns, {
    pinnedColumnKeys: {
      prepend: ["select"],
    },
    insertedColumnKeys: [{ key: "actions", index: 8 }],
  });
};

const ApprovalTable = ({
  contentHeight,
  onApprovalModalOpen,
  deductions,
  displayMode = "standalone",
}: ApprovalTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();
  const {
    setDrawerOpen,
    setDrawerInfo,
    drawerInfo,
    setCurrentExpandedDeductionId,
    currentExpandedDeductionId,
    hasAnyUnsavedChanges,
    setHasAnyUnsavedChanges,
  } = useDeductionDrawer();
  const { settings: workspaceSettings } = useWorkspace();
  const { hasAllPermissions, hasAnyPermission } = useUserPermissions();
  const {
    isRefreshing,
    approvals: storeApprovals,
    columns,
    filterInfo,
    setSelectedApprovals,
    selectedApprovals,
    selectedApproval,
    setSelectedApproval,
    isLoading,
  } = useApproval();

  const { getStagesLookup } = useLookup();

  const isExpandedTable = displayMode === "expanded-table";
  const approvals = deductions ?? storeApprovals;
  useEffect(() => {
    if (isRefreshing || isExpandedTable) {
      return;
    }
    const container = tableContainerRef.current?.parentElement;
    if (container) {
      container.scrollTo({
        top: 0,
      });
    }
  }, [isRefreshing, isExpandedTable]);

  useEffect(() => {
    getStagesLookup();
  }, [getStagesLookup]);

  const showUnSavedConfirmation = useCallback(async (): Promise<boolean> => {
    return confirmDiscardUnsavedChanges({
      hasAnyUnsavedChanges,
      confirm,
      setHasAnyUnsavedChanges,
    });
  }, [hasAnyUnsavedChanges, confirm, setHasAnyUnsavedChanges]);

  const handleRowClick = useCallback(
    async (record: Deduction) => {
      await openDeductionDrawerFromRow({
        record,
        showUnsavedConfirmation: showUnSavedConfirmation,
        setCurrentExpandedDeductionId,
        setDrawerInfo,
        setDrawerOpen,
      });
    },
    [setDrawerInfo, setDrawerOpen, setCurrentExpandedDeductionId, showUnSavedConfirmation]
  );

  const searchKeywords = useMemo(() => parseSearchKeywords(filterInfo.search), [filterInfo.search]);

  const handleSelectCheckbox = useCallback(
    (record: Deduction, checked: boolean) => {
      handleSelectRowCheckbox({
        checked,
        record,
        selectedItems: selectedApprovals,
        setSelectedItems: setSelectedApprovals,
        clearSingleSelection: () => setSelectedApproval(null),
        isSameItem: (item, currentRecord) =>
          item.claimNumber === currentRecord.claimNumber && item.remittanceItemId === currentRecord.remittanceItemId,
      });
    },
    [selectedApprovals, setSelectedApprovals, setSelectedApproval]
  );

  const approvalTableColumns = useMemo(() => {
    return getColumns({
      workspaceSettings,
      hasAllPermissions,
      hasAnyPermission,
      columns,
      isRefreshing,
      searchKeywords,
      onRowSelect: handleRowClick,
      onSelectCheckbox: handleSelectCheckbox,
      selectedApprovals,
      onApprovalModalOpen,
    });
  }, [
    workspaceSettings,
    hasAllPermissions,
    hasAnyPermission,
    columns,
    isRefreshing,
    searchKeywords,
    handleRowClick,
    handleSelectCheckbox,
    selectedApprovals,
    onApprovalModalOpen,
  ]);

  const blankDeductions = useMemo(() => getBlankDeductions(25), []);

  const dataSource = useMemo(() => {
    if (isExpandedTable) {
      return approvals;
    }
    if (isRefreshing || (approvals?.length === 0 && !isLoading)) {
      return blankDeductions;
    }
    return approvals;
  }, [isRefreshing, blankDeductions, approvals, isLoading, isExpandedTable]);

  return (
    <Table
      className="approval-table"
      ref={tableContainerRef}
      rowHoverable={true}
      sticky={!isExpandedTable}
      border={isExpandedTable ? "none" : "row"}
      aria-label="Approvals Table"
      aria-labelledby="approvals-table"
      scroll={{ x: "100%", ...(isExpandedTable ? {} : { y: contentHeight }) }}
      columns={approvalTableColumns}
      dataSource={dataSource}
      rowClassName={(_key, record: Deduction, index: number) => {
        const classes: string[] = [];

        if (hasAnyPermission(APPROVAL_VIEW_PERMISSIONS)) {
          classes.push("table-row-clickable");
        }
        if (record.id === currentExpandedDeductionId || selectedApproval?.id === record.id) {
          classes.push("current-remittance-item");
        }
        if (selectedApprovals.some(item => item.id === record.id)) {
          classes.push("table-row-selected-approval");
        }

        const currentIndex = approvals.findIndex(item => item.claimNumber === drawerInfo?.invoiceNumber);
        if (currentIndex === index) {
          classes.push("table-row-focused");
        } else if (currentIndex === index + 1) {
          classes.push("table-row-focused-prev");
        } else if (currentIndex === index - 1) {
          classes.push("table-row-focused-next");
        }

        return classes.join(" ");
      }}
    />
  );
};

export { ApprovalTable };
